import React, { useState, useEffect } from 'react'
import {
  Edit,
  TextInput,
  useTranslate,
  useEditController,
  TabbedForm,
  email,
  FormTab,
  useNotify,
  useRedirect,
  required,
  FormDataConsumer,
  TabbedFormTabs,
  SelectInput,
  useGetIdentity,
} from 'react-admin'
import { Grid, Skeleton } from '@mui/material'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import { PERMISSION_SHOW } from 'src/helper/Permission'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { WrapperToPermission } from 'src/components/pages/authentication/user/logic/WrapperToPermission'
import { WrapperClient } from 'src/components/pages/authentication/user/logic/WrapperClient'
import { WrapperApplication } from 'src/components/pages/authentication/user/logic/WrapperApplication'
import { WrapperRoles } from 'src/components/pages/authentication/user/logic/WrapperRoles'
import { generateRandomKey } from 'src/helper/generateRandomKey'
import { FieldsByApplication } from 'src/components/pages/authentication/user/logic/FieldsByApplication'
import { Add, Delete } from '@mui/icons-material'
import { userServices } from 'src/services/auth/user/user.service'
import Button from '@mui/material/Button'
import { TableAppByClient } from 'src/components/pages/authentication/user/logic/edit/TableAppByClient'
import { TableDevicesByClient } from 'src/components/pages/authentication/user/logic/edit/TableDevicesByClient'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { BooleanInput } from 'src/components/uiElements/fields/BooleanInput'
import { WrapperDashboard } from 'src/components/pages/authentication/user/logic/WrapperDashboard'
import { WrapperConfig } from 'src/components/pages/authentication/user/logic/WrapperConfig'
import { LoadingPermissionProvider } from 'src/components/pages/authentication/user/context/LoadingPermissionContext'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'

export const UserEdit = (props) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const { identity } = useGetIdentity()
  const { record, isLoading, isFetching } = useEditController()
  const [arrayInput, setArrayInput] = useState([])
  const [loadinUserbyClient, setLoadinUserbyClient] = useState(true)
  const [listClientApp, setListClientApp] = useState([])
  const [isSuperadmin, setIsSuperadmin] = useState(false)
  const [listDevicesByClient, setListDevicesByClient] = useState([])
  const hasShowDevicesPermissions = hasPermissionFunction('devices-by-user', PERMISSION_SHOW)
  const [selectedClienthash, setSelectedClientHash] = useState(null)
  const timezones = Intl.supportedValuesOf('timeZone').map(tz => ({
    id: tz,
    name: tz,
  }))
  const [filteredOptions, setFilteredOptions] = useState(timezones)
  useEffect(() => {
    ;(async () => {
      if (record && record?.id) {
        setIsSuperadmin(record.is_superadmin)
        setLoadinUserbyClient(true)
        try {
          const list = await userServices.userByClientApp(record?.id)
          setListClientApp(list)
        } catch (error) {
          setListClientApp([])
        } finally {
          setLoadinUserbyClient(false)
        }
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record])

  useEffect(()=>{
    if(hasShowDevicesPermissions){
      userServices.devicesByUserFilter({
        user_id:record?.id,
        operator:'equal',
        limit:50,
      })
      .then(res=>{
        setListDevicesByClient(res)
      })
      .catch(err=>{
        setListDevicesByClient([])
      })
    }
    
  },[isSuperadmin])

  const addList = () => {
    setArrayInput((prev) => ([
      ...prev,
      {
        id: generateRandomKey(9),
        clients: { optionsClient: [], checkedClient: identity.client_id },
        applications: { optionsApplication: [], checkedApplication: 1 },
        roles: { optionsRole: [], selectedRole: [] },
        permissions: { left: [], right: [] },
        dashboard: null,
        config: {},
      },
    ]))
  }

  const removeList = (id) => {
    setArrayInput((prev) => prev.filter((item) => item.id !== id))
  }

  const listApplications = () => {
    let applications = []
    let listApplications = [...arrayInput]
    for (const application of listApplications) {
      if (Object.keys(application).length !== 0) {
        const configByApp = application.allApplicationToFilter?.find((elem) => (
          elem.application_id === application.applications.checkedApplication
        ))

        applications.push({
          client_id: application.clients.checkedClient,
          application_id: application.applications.checkedApplication,
          role_id: application.roles.selectedRole,
          permission_id: application.permissions.right.map((item) => item.id),
          endpoint: configByApp?.config?.reactAdmin?.components?.user?.edit?.endpoint,
          field_application: application.field_application,
          dashboard: application?.dashboard,
          config: application?.config,
        })
      }
    }
    return applications
  }

  const transform = (data) => ({
    firstname: data.firstname,
    mail: data.mail,
    surname: data.surname,
    username: data.username,
    is_active: data.is_active,
    language: data.language,
    is_staff: data.is_staff || false,
    is_admin: data.is_admin || false,
    is_superadmin: data.is_superadmin || false,
    timezone: data.timezone,
    applications: listApplications(),
  })

  const onSuccess = () => {
    notify('ra.notification.updated_success_masculine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.user') },
    })
    redirect('/user')
  }

  const onError = () => {
    notify('ra.notification.updated_error_masculine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.user') },
    })
  }

  const onFilterOptions = (value) =>{
    const newValue = value.target?.value
    setTimeout(() => {
      if(newValue){
        setFilteredOptions(timezones.filter(timezone=>timezone.toLocaleLowerCase()?.includes(newValue)))
      } else {
        setFilteredOptions(timezones)
      }
    }, 1000);
  }

  return (
    <Edit
      {...props}
      transform={transform}
      mutationMode='pessimistic'
      mutationOptions={{ onSuccess, onError }}
      actions={null}
    >
      <CardCommon title={translate('ra.title.edit_config')}>
        <TabbedForm
          toolbar={<CardCrudBottomButtons btnSave={true} loading={isLoading || isFetching} />}
          tabs={<TabbedFormTabs variant='scrollable' scrollButtons='auto' />}
        >
          <FormTab label={translate('ra.entity.user')}>
            <Grid container spacing={3}>
              <EditInputsWrap label={'Id'}>
                <TextInput disabled source='id' label='' fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.firstname')} required>
                <TextInput source='firstname' label='' validate={[required()]} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.surname')} required>
                <TextInput source='surname' label='' validate={[required()]} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.username')} required>
                <TextInput disabled source='username' label='' validate={[required()]} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.email')} required>
                <TextInput
                  type='email'
                  source='mail'
                  label=''
                  fullWidth
                  validate={[required(), email('invalid email')]}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.language')} required>
                <SelectInput
                  source='language'
                  optionText='name'
                  optionValue='key'
                  fullWidth
                  choices={[
                    { name: translate('ra.language.spanish'), key: 'es-AR' },
                    { name: translate('ra.language.english'), key: 'en-US' },
                    { name: translate('ra.language.pt_br'), key: 'pt-BR' },
                  ]}
                  label=''
                  validate={[required()]}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.timezone')} required>
                <AutocompleteInput
                  source='timezone'
                  options={
                    filteredOptions
                  }
                  id='timezone'
                  size='small'
                  optionsText={['name']}
                  label={false}
                  optionValue='id'
                  useOptionValue
                  onSearch={onFilterOptions}
                  resetOnLoad={false}
                  fields={['name']}
                  validate={required()}
                  disablePortal={false}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.is_active')}>
                <BooleanInput
                  label={translate('ra.field.is_active')}
                  source='is_active'
                />
              </EditInputsWrap>
              {(identity?.is_superadmin || identity?.is_admin) && (
                <EditInputsWrap label={translate('ra.field.is_admin')}>
                  <BooleanInput
                    label={translate('ra.field.is_admin')}
                    source='is_admin'
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setIsSuperadmin(false)
                      }
                    }}
                  />
                </EditInputsWrap>
              )}
              {identity?.is_superadmin && (
                <>
                  <EditInputsWrap label={translate('ra.field.is_staff')}>
                    <BooleanInput
                      label={translate('ra.field.is_staff')}
                      source='is_staff'
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setIsSuperadmin(false)
                        }
                      }}
                    />
                  </EditInputsWrap>
                  <FormDataConsumer>
                    {({ formData }) => (
                      <EditInputsWrap label={translate('ra.field.is_superadmin')}>
                        <BooleanInput
                          label={translate('ra.field.is_superadmin')}
                          source='is_superadmin'
                          disabled={!formData.is_staff}
                          value={isSuperadmin}
                          onChange={(e) => setIsSuperadmin(e.target.checked)}
                        />
                      </EditInputsWrap>
                    )}
                  </FormDataConsumer>
                </>
              )}
            </Grid>
          </FormTab>
          <FormTab label={translate('ra.entity.config_application')}>
            {loadinUserbyClient ? (
              <Skeleton variant='rectangular' width={'100%'} height={40} />
            ) : (
              <TableAppByClient list={listClientApp} setList={setListClientApp} hashuser={record.hashuser} />
            )}
            {React.Children.toArray(
              arrayInput.map((item, i) => (
                Object.keys(item).length !== 0 ? (
                  <LoadingPermissionProvider key={i}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4} className='uiInputLabel'>
                        <WrapperClient setSelectedHashClient={setSelectedClientHash} index={i} setList={setArrayInput} item={item} id={item.id} />
                        <WrapperApplication
                          index={i}
                          setList={setArrayInput}
                          item={item}
                          id={item.id}
                        />
                        <EditInputsWrap label={translate('ra.field.dashboard')} md={12}>
                          <WrapperDashboard index={i} setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                        <EditInputsWrap
                          label={translate('ra.field.json')}
                          md={12}
                          style={{ paddingBottom: '20px' }}
                        >
                          <WrapperConfig index={i} setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.role')} md={12}>
                          <WrapperRoles index={i} setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                        <FieldsByApplication selectedHashClient={selectedClienthash} index={i} setList={setArrayInput} item={item} id={item.id} />
                      </Grid>
                      <Grid item xs={12} sm={8} className='uiInputLabel'>
                        <EditInputsWrap
                          // tooltiptext={translate('ra.message.permission_tooltip')}
                          label={translate('ra.field.permission')}
                          md={12}
                        >
                          <WrapperToPermission setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
                      <Grid item xs={12} sm={12} className='grid'>
                        <Button
                          onClick={() => removeList(item.id)}
                          startIcon={<Delete />}
                          color='error'
                          variant='outlined'
                        >
                          {translate('ra.action.delete')}
                        </Button>
                      </Grid>
                    </Grid>
                  </LoadingPermissionProvider>
                ) : null
              ))
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12}>
                <Button startIcon={<Add />} onClick={addList}>
                  {translate('ra.action.add_specific', {
                    name: translate('ra.field.applications'),
                  })}
                </Button>
              </Grid>
            </Grid>
          </FormTab>
          {hasShowDevicesPermissions && (
            <FormTab label={translate('ra.field.all_devices')}>
            { loadinUserbyClient ? (
              <Skeleton variant='rectangular' width={'100%'} height={40} />
            ) : (
              <TableDevicesByClient list={listDevicesByClient} setList={setListDevicesByClient}/>
            )}
          </FormTab> )}
        </TabbedForm>
      </CardCommon>
    </Edit>
  )
}
